import { X } from "lucide-react";
import { format } from "date-fns";
import { useImportantDates } from "@/services/settings";

const CATEGORY_COLOR_MAP: Record<string, string> = {
  deloitte: "#A2C593",
  "us-holiday": "#9BBFE4",
  "us holiday": "#9BBFE4",
  religious: "#CCC0DE",
  "unavailable-booking": "#EC9089",
  unavailable: "#EC9089"
};

function mapCategoryColor(cssClass: string): string {
  if (!cssClass) return "#BBBCBC";
  const key = cssClass.toLowerCase().replace(/_/g, "-");
  for (const [k, v] of Object.entries(CATEGORY_COLOR_MAP)) {
    if (key.includes(k)) return v;
  }
  return "#BBBCBC";
}

const LEGEND_ITEMS = [
  { color: "#A2C593", label: "Deloitte Day" },
  { color: "#9BBFE4", label: "U.S. Holiday" },
  { color: "#CCC0DE", label: "Religious Holiday" },
  { color: "#EC9089", label: "Unavailable for Booking" }
];

interface ImportantDatesDialogProps {
  open: boolean;
  onClose: () => void;
}

export function ImportantDatesDialog({ open, onClose }: ImportantDatesDialogProps) {
  const { data: dates = [], isPending } = useImportantDates();

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: "rgba(0,0,0,0.4)" }}
      onClick={onClose}
      onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}
      tabIndex={-1}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="holiday-dialog-title"
        className="flex flex-col bg-white"
        style={{ width: 750, height: 485, borderRadius: 8, boxShadow: "0 4px 20px rgba(0,0,0,0.15)", overflow: "hidden" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between"
          style={{ background: "#ffffff", borderBottom: "1px solid #e5e5e5", padding: "16px 20px" }}
        >
          <h2 id="holiday-dialog-title" style={{ fontSize: 16, fontWeight: 600, color: "#000", margin: 0 }}>
            Important Dates &amp; Holidays
          </h2>
          <button type="button" onClick={onClose} aria-label="Close"
            style={{ background: "none", border: "none", cursor: "pointer", lineHeight: 1 }}>
            <X size={16} color="black" />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: 20, flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {isPending ? (
            <p style={{ fontSize: 14, color: "#63666A" }}>Loading…</p>
          ) : dates.length === 0 ? (
            <p style={{ fontSize: 14, color: "#63666A" }}>No important dates available.</p>
          ) : (
            <div style={{ flex: 1, overflowY: "auto", marginBottom: 8 }}>
              <table style={{ borderCollapse: "collapse", width: "100%", border: 0 }}>
                <tbody>
                  {dates.map((date) => (
                    <tr key={date.id}>
                      <td style={{ padding: "8px", border: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <span style={{
                            width: 12, height: 12, borderRadius: "50%", flexShrink: 0,
                            backgroundColor: mapCategoryColor(date.categoryHeaderCssClass)
                          }} />
                          <span style={{ fontSize: 14, color: "#000", fontWeight: 400 }}>{date.title}</span>
                        </div>
                      </td>
                      <td style={{ padding: "8px", border: 0, fontSize: 14, color: "#000", fontWeight: 400, whiteSpace: "nowrap" }}>
                        {date.begin
                          ? (() => { try { return format(new Date(date.begin), "EEE, MMMM d, yyyy"); } catch { return date.begin; } })()
                          : "—"}
                      </td>
                      <td style={{ padding: "8px", border: 0, display: "flex", alignItems: "center" }}>
                        {date.doNotBook === true ? (
                          <span title="Unavailable for booking" style={{
                            width: 12, height: 12, borderRadius: "50%", flexShrink: 0,
                            backgroundColor: "#EC9089", marginRight: 8
                          }} />
                        ) : (
                          <span title="Available for booking" style={{
                            width: 12, height: 12, borderRadius: "50%", flexShrink: 0,
                            backgroundColor: "transparent", border: "1px solid #bdc3c7", marginRight: 10
                          }} />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer legend */}
        <div style={{
          display: "flex", flexWrap: "wrap", gap: 16, padding: 5, marginLeft: 14,
          borderTop: "1px solid #e5e5e5", paddingTop: 10, paddingBottom: 10
        }}>
          {LEGEND_ITEMS.map(({ color, label }) => (
            <div key={label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ width: 12, height: 12, borderRadius: "50%", flexShrink: 0, backgroundColor: color }} />
              <span style={{ fontSize: 12, color: "#000", fontWeight: 400 }}>{label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
